import React, { useCallback, useEffect, useState } from 'react';

import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import DatePicker from 'react-datepicker';
import 'src/styles/react-datepicker.css';

import './MonthRangePicker.scss';
import { dateToString, isValidRange, stringToDate } from './utils';

export interface MonthRangePickerProps {
    /** Start month in YYYY-MM format */
    startMonth: string;
    /** End month in YYYY-MM format */
    endMonth: string;
    /** Callback when Load Data button is clicked with selected range */
    onChange: (startMonth: string, endMonth: string) => void;
    /** Text for Load Data button (default: "Load Data") */
    loadButtonText?: string;
    /** Disable Load Data button */
    loadButtonDisabled?: boolean;
    /** Label text for the picker */
    label?: string;
    /** CSS class name for the picker container */
    className?: string;
    /** Width of the date picker input (default: "180px") */
    pickerWidth?: string;
    /** Disable the entire component */
    disabled?: boolean;
}

export const MonthRangePicker: React.FC<MonthRangePickerProps> = ({
    startMonth,
    endMonth,
    onChange,
    loadButtonText = 'Load Data',
    loadButtonDisabled = false,
    label,
    className = '',
    pickerWidth = '180px',
    disabled = false
}) => {
    // Internal state for pending selection
    const [pendingStartDate, setPendingStartDate] = useState<Date | null>(null);
    const [pendingEndDate, setPendingEndDate] = useState<Date | null>(null);

    // Initialize internal state from props
    useEffect(() => {
        setPendingStartDate(stringToDate(startMonth));
        setPendingEndDate(stringToDate(endMonth));
    }, [startMonth, endMonth]);

    // Handle date range change from DatePicker
    const handleDateChange = useCallback(([newStartDate, newEndDate]: [Date | null, Date | null]) => {
        // Validate range: if end is before start, reset both
        if (newStartDate && newEndDate && newEndDate < newStartDate) {
            setPendingStartDate(null);
            setPendingEndDate(null);
            return;
        }

        setPendingStartDate(newStartDate);
        setPendingEndDate(newEndDate);
    }, []);

    // Handle Load Data button click
    const handleLoadClick = useCallback(() => {
        if (pendingStartDate && pendingEndDate) {
            const startStr = dateToString(pendingStartDate);
            const endStr = dateToString(pendingEndDate);

            if (isValidRange(startStr, endStr)) {
                onChange(startStr, endStr);
            }
        }
    }, [pendingStartDate, pendingEndDate, onChange]);

    // Check if current selection is valid
    const isValidSelection = pendingStartDate && pendingEndDate && pendingEndDate >= pendingStartDate;

    return (
        <Row className={className}>
            {label && <Text type="b300">{label}</Text>}
            <div style={{ width: pickerWidth }}>
                <DatePicker
                    className="month-range-filter-input"
                    selected={pendingStartDate}
                    onChange={handleDateChange}
                    selectsRange
                    startDate={pendingStartDate}
                    endDate={pendingEndDate}
                    dateFormat="yyyy-MM"
                    showMonthYearPicker
                    disabled={disabled}
                />
            </div>
            <Button
                size="medium"
                onClick={handleLoadClick}
                disabled={!isValidSelection || loadButtonDisabled || disabled}
            >
                {loadButtonText}
            </Button>
        </Row>
    );
};
